import React, { useEffect, useMemo, useState } from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { getMoment } from '@libs/NwMoment';
import { datePickerToApi, getNow } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadExternalJob } from '@hooks/API/Event/UseExternalJob';
import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import {
    NWInput,
    NWTextAreaCharCounter,
    NWContactWithBillingInfo,
    NWDatePicker,
    NWContactDropdown,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import { getTransactionContactBillingInfo } from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';

import AmountLine from './Common/AmountLine';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const CommAsMothAgForm = ({ externalJobData, agencyID, onCloseDrawer, onTransactionCreated, width, minWidth }) => {
    const transactionProperties = useTransactionLabels('CommAsMothAg');
    const defaultTypeQuantity = transactionProperties.startLabel.TypeQuantityName
        ? transactionProperties.startLabel.TypeQuantityName
        : 'Units';
    const configurations = useConfigurations();
    const [clientBillingInfos, setClientBillingInfos] = useState(null);

    useEffect(() => {
        const fetchBillingInfo = async (contactID) => {
            const billingInfos = await getTransactionContactBillingInfo(contactID);
            setClientBillingInfos(billingInfos);
        };

        if (externalJobData) {
            fetchBillingInfo(externalJobData.AgencyID);
        }
        if (agencyID) {
            fetchBillingInfo(agencyID);
        }
    }, [externalJobData, agencyID]);

    const saveTransaction = async (values, setSubmitting) => {
        const apiData = {
            ExtJobID: externalJobData ? externalJobData.ID : null,
            AgencyID: externalJobData ? externalJobData.AgencyID : agencyID,
            AgencyBillingInfoID: values.ContactToBill.ContactBillingInfoID,
            ModelID: externalJobData ? externalJobData.ModelID : values.ModelID,
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            Quantity: values.Quantity[0],
            TypeQuantity: values.Quantity[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
        };

        try {
            await Axios.post('/transactions/CommAsMothAg', apiData);
            if (externalJobData) {
                forceReloadExternalJob(externalJobData.ID);
            }
            if (onTransactionCreated) {
                onTransactionCreated();
            }
            onCloseDrawer();
            setSubmitting(false);
        } catch (error) {
            console.error('Create Commission Error => ', error);
            setSubmitting(false);
        }
    };

    const clientBillingInfoDefaultID = useMemo(() => {
        if (!clientBillingInfos) return null;
        const clientBillingInfoDefault = clientBillingInfos.find((item) => item.isDefault);
        let cbiID = null;
        if (clientBillingInfoDefault) {
            cbiID = clientBillingInfoDefault.ID;
        } else {
            if (clientBillingInfoDefault && clientBillingInfoDefault.length > 0) {
                cbiID = clientBillingInfoDefault[0].ID;
            }
        }
        return cbiID;
    }, [clientBillingInfos]);

    const initialValues = useMemo(() => {
        return {
            ContactToBill: {
                ContactID: externalJobData ? externalJobData.AgencyID : agencyID,
                ContactBillingInfoID: clientBillingInfoDefaultID,
            },
            TransactionLabelID: transactionProperties.startLabel.ID,
            Title: transactionProperties.startLabel.Label,
            Description: '',
            ModelID: externalJobData ? externalJobData.ModelID : null,
            RefDate: externalJobData && externalJobData.StartDate ? getMoment(externalJobData.StartDate) : getNow(),
            Amount: [configurations.defaultCurrency, null],
            Quantity: [1, defaultTypeQuantity],
        };
    }, [
        clientBillingInfoDefaultID,
        configurations.defaultCurrency,
        defaultTypeQuantity,
        externalJobData,
        agencyID,
        transactionProperties.startLabel.ID,
        transactionProperties.startLabel.Label,
    ]);

    const getAgencyIDArray = () => {
        const agID = externalJobData ? externalJobData.AgencyID : agencyID;
        if (agID) {
            return [{ ID: agID, Name: getContactName(agID) }];
        }
        return [];
    };

    return clientBillingInfos ? (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
                ContactToBill: Yup.string().required('Agency is required'),
                ModelID: externalJobData
                    ? Yup.string().nullable()
                    : Yup.string().nullable().required('Model is required'),
            })}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        {/* <Row gutter={32}>
                                <Col span={14} className='rate-form'> */}
                        <TransactionLabelsSelector
                            values={values}
                            setFieldValue={setFieldValue}
                            transactionProperties={transactionProperties}
                            updateTitle
                        />
                        <Field
                            component={NWContactWithBillingInfo}
                            value={values.ContactToBill}
                            label="Agency"
                            name="ContactToBill"
                            contacts={getAgencyIDArray()}
                            billingInfos={clientBillingInfos}
                            disabled
                            required
                        />
                        {externalJobData ? (
                            <div className="nw-form-item-container">
                                <div className="ant-form-item ant-form-item-with-help true ant-form-item-has-success">
                                    <div className="ant-row ant-form-item-row">
                                        <div className="ant-col ant-form-item-label">
                                            <label
                                                className="ant-form-item-required ant-form-item-no-colon"
                                                title="Model"
                                            >
                                                Model
                                            </label>
                                        </div>
                                        <div className="ant-col ant-form-item-control">
                                            <div className="ant-form-item-control-input">
                                                <div className="ant-form-item-control-input-content">
                                                    <div>
                                                        <h3>{getContactName(externalJobData.ModelID)}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Field
                                component={NWContactDropdown}
                                label="Model"
                                name="ModelID"
                                activeTypes={['mod', 'sco']}
                                value={values.ModelID}
                                required
                            />
                        )}
                        <Field
                            component={NWDatePicker}
                            label="Date"
                            name="RefDate"
                            format="MMM DD, YYYY"
                            allowClear={false}
                            value={values.RefDate}
                        />
                        <Field
                            component={NWInput}
                            label="Title"
                            name="Title"
                            type="text"
                            value={values.Title}
                            maxLength={255}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Description"
                            name="Description"
                            type="text"
                            value={values.Description}
                            maxLength={2500}
                            autoSize={{ minRows: 3, maxRows: 10 }}
                        />
                        <AmountLine values={values} touched={touched} amountLabel="Commission Amount" />
                        {/* </Col>
                            </Row> */}
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    ) : (
        <Loading />
    );
};

export default CommAsMothAgForm;
